import React from 'react';
import { formatEther, formatUnits } from 'viem'
import { PendingSlapList, Objective, Board, ObjectiveGroup } from '../utils/Primitives';
import { getPaymentMethod } from '../payments/PaymentMethods';
import AllowanceNeeded from '../payments/AllowanceNeeded';
import FundsNeeded from '../payments/FundsNeeded';
import { zeroAddress } from 'viem';

export interface SlapWarning {
  issueCode: number;
  recordId: bigint;
  value: bigint;
}

interface SlapCheckWarningsProps {
  boardRef: React.MutableRefObject<Board>;
  warnings: SlapWarning[];
  pendingSlaps: PendingSlapList;
  objectives: ObjectiveGroup;
  variant?: 'light' | 'dark';
}

const SlapCheckWarnings: React.FC<SlapCheckWarningsProps> = ({
  boardRef,
  warnings,
  pendingSlaps,
  objectives,
  variant = 'light',
}) => {
  if (warnings.length === 0) {
    return null;
  }

  const getStickerSlapReasonMessage = (value: bigint): string => {
    switch (Number(value)) {
      case 101:
        return "Sticker is capped";
      case 102:
        return "Sticker is sold out";
      case 103:
        return "Player has zero balance of the gate token";
      case 404:
        return "Sticker not found";
      case 505:
        return "ERC20 payment failed";
      case 411:
        return "Gate check failed";
      default:
        return "Unknown reason";
    }
  };

  const getWarningMessage = (warning: SlapWarning): string => {
    switch (warning.issueCode) {
      case 0:
        return `Invalid Place (ID: ${warning.recordId}).`;
      case 1:
        return `Player is Banned.`;
      case 2:
        const affectedSlap = pendingSlaps.find(slap => BigInt(slap.sticker.id) === warning.recordId);
        const reason = getStickerSlapReasonMessage(warning.value);
        return `Player Cannot Slap Sticker ${warning.recordId}: "${affectedSlap?.sticker.name || 'Name Unknown'}". ${reason}.`;
      case 3:
        const objective = objectives[Number(warning.recordId)];
        return `Objective "${objective?.name || 'Unknown'}" (ID: ${warning.recordId}) is not active.`;
      case 4:
        if (warning.recordId === BigInt(0)) {
          return `Your account has insufficient ETH on Base to make this slap. Additional ${formatEther(warning.value)} ETH required.`;
        } else {
          const paymentMethod = getPaymentMethod({ boardRef, paymentMethodId: Number(warning.recordId) });
          const symbol = paymentMethod?.symbol || `Payment Method ${warning.recordId}`;
          const amount = paymentMethod
            ? formatUnits(warning.value, paymentMethod.decimals)
            : warning.value.toString();
          return `Insufficient $${symbol} balance. Additional ${amount} $${symbol} required.`;
        }
      case 5:
        const paymentMethod = getPaymentMethod({ boardRef, paymentMethodId: Number(warning.recordId) });
        const symbol = paymentMethod?.symbol || `Token ID: ${warning.recordId}`;
        const amount = paymentMethod
            ? formatUnits(warning.value, paymentMethod.decimals)
            : warning.value.toString();
        return `Insufficient $${symbol} allowance. Additional ${amount} $${symbol} allowance required.`;
      default:
        return `Unknown issue (Code: ${warning.issueCode}, Record ID: ${warning.recordId}, Value: ${warning.value}).`;
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {warnings.map((warning, index) => (
        <div
          key={index}
          className={[
            'p-4 rounded-lg border shadow-sm',
            variant === 'dark'
              ? 'border-rose-400/60 bg-rose-950/60 text-rose-100'
              : 'border-rose-300 bg-rose-50 text-rose-900',
          ].join(' ')}
        >
          <p
            className={[
              'font-semibold mb-2',
              variant === 'dark' ? 'text-rose-200' : 'text-rose-700',
            ].join(' ')}
          >
            Warning:
          </p>
          <p
            className={[
              warning.issueCode === 4 ? 'text-sm' : '',
              'mb-1',
              variant === 'dark' ? 'text-rose-100' : 'text-rose-800',
            ].join(' ')}
          >
            {getWarningMessage(warning)}
          </p>
          {warning.issueCode === 4 && (
            (() => {
              const paymentMethodId = Number(warning.recordId);
              const paymentMethod = getPaymentMethod({ boardRef, paymentMethodId }) || (
                paymentMethodId === 0
                  ? {
                      id: 0,
                      address: zeroAddress,
                      name: 'ETH',
                      symbol: 'ETH',
                      decimals: 18,
                    }
                  : null
              );
              if (!paymentMethod) {
                return <p>Payment method not found for ID: {warning.recordId.toString()}</p>;
              }
              return <FundsNeeded paymentMethod={paymentMethod} />;
            })()
          )}
          {warning.issueCode === 5 && (
            (() => {
              const paymentMethod = getPaymentMethod({ boardRef, paymentMethodId: Number(warning.recordId) });
              if (!paymentMethod) {
                return <p>Payment method not found for ID: {warning.recordId.toString()}</p>;
              }
              return (
                <AllowanceNeeded
                  paymentMethod={paymentMethod}
                  amount={warning.value * BigInt(8)}
                  onAllowanceSet={() => console.log('Allowance set!')}
                />
              );
            })()
          )}
        </div>
      ))}
    </div>
  );
};

export default SlapCheckWarnings;
